import React, { useMemo, useState } from "react";

import {
  faCalculatorSimple,
  faMinus,
  faPlus,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
  RowData,
} from "@tanstack/react-table";
import { useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { HighlightedText } from "src/base-components/HighlightedText";
import { MarkdownRenderer } from "src/base-components/MarkdownRenderer/MarkdownRenderer";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { Cell, Header, TableComp } from "src/base-components/Table";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { FeatureActionsMenu } from "src/featureCatalogue/Features/FeatureActionsMenu";
import {
  EntityFilter,
  EventTypeFilter,
  StatusFilter,
} from "src/featureCatalogue/Features/FilterActions";
import {
  DisplaySqlCell,
  QUERY_FILTER_CONFIG,
} from "src/featureCatalogue/Queries/Queries";
import { QueryStatusPill } from "src/featureCatalogue/Queries/QueryStatusPill";
import { useFeatureQueries, useFeatures } from "src/featureCatalogue/queries";
import { TableActions } from "src/featureCatalogue/TableActions";
import { useFilteredData } from "src/featureCatalogue/useFilteredData";
import {
  commonColumns,
  EntityPills,
  Feature,
  FilterOptions,
  Query,
  statusColumns,
} from "src/featureCatalogue/utils";
import { useEnvironment } from "src/hooks/useEnvironment";
import { WhitePane } from "src/jobs/common/WhitePane";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import {
  DashboardPageParams,
  getUrlToManageFeatureQueryPage,
} from "src/router/urls";
import { getTypeIcon } from "src/utils/icons";
import { speakPythonPrimitive } from "src/utils/speakPython";
import { useParamsDecode } from "src/utils/useParamsDecode";

const columnHelper = createColumnHelper<Feature>();
const queryColumnHelper = createColumnHelper<Query>();

const HighlightedCellText = <D extends RowData>({
  info,
}: {
  info: CellContext<D, string | null | undefined>;
}) => (
  <HighlightedText
    highlight={info.table.getState().globalFilter}
    highlightClassName="font-inter-semibold-12px bg-yellow-200"
    text={info.getValue()}
  />
);

// Shared column definitions
const nameColumn = (headerText: string, options?: { sticky?: "left" }) =>
  columnHelper.accessor("name", {
    header: (info) => <Header info={info}>{headerText}</Header>,
    cell: (info) => {
      const name = info.getValue();
      return (
        <Cell className="gap-x-0.5" info={info}>
          <Icon
            icon={
              info.row.original.feature_type
                ? getTypeIcon(info.row.original.feature_type, faMinus)
                : faMinus
            }
            size="xs"
          />
          <Tooltip placement="top" title={name} asChild>
            <div className="truncate">
              <HighlightedCellText info={info} />
            </div>
          </Tooltip>
        </Cell>
      );
    },
    maxSize: 300,
    minSize: 300,
    filterFn: "includesString",
    meta: options?.sticky ? { sticky: options.sticky } : undefined,
  });

const keyColumn = (headerText: string) =>
  columnHelper.accessor("key", {
    header: (info) => <Header info={info}>{headerText}</Header>,
    cell: (info) => (
      <Cell info={info}>
        <Pill size="sm" variant="gray">
          <Pill.Text>
            <HighlightedCellText info={info} />
          </Pill.Text>
        </Pill>
      </Cell>
    ),
    maxSize: 300,
    minSize: 300,
    filterFn: "includesString",
  });

const descriptionColumn = () =>
  columnHelper.accessor("description", {
    header: (info) => <Header info={info}>Description</Header>,
    cell: (info) => (
      <DisplayDescriptionCell info={info} value={info.getValue()} />
    ),
    maxSize: 200,
    minSize: 200,
    filterFn: "includesString",
  });

const entityTypesColumn = (entityTypeOptions?: string[]) => {
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  return columnHelper.accessor("entity_types", {
    header: (info) => (
      <Header info={info}>
        <div
          className={twJoin(
            "flex items-center",
            featureLifecycleEnabled ? "gap-x-2" : "gap-x-4",
          )}
        >
          <span>Entity</span>
          <EntityFilter column={info.column} options={entityTypeOptions} />
        </div>
      </Header>
    ),
    cell: (info) => (
      <Cell className="flex space-x-2" info={info}>
        <EntityPills entities={info.getValue() ?? []} />
      </Cell>
    ),
    minSize: 150,
    maxSize: 150,
    filterFn: "includesString",
  });
};

const featureDefaultColumn = () =>
  columnHelper.accessor("feature_default", {
    header: (info) => (
      <Header info={info}>
        <span className="whitespace-nowrap">Default Value</span>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>{speakPythonPrimitive(info.getValue() ?? "None")}</Cell>
    ),
    minSize: 130,
    maxSize: 130,
  });

const actionsMenuColumn = (options?: { sticky?: "right" }) =>
  columnHelper.display({
    id: "actions-menu",
    header: (info) => (
      <Header info={info}>
        <span />
      </Header>
    ),
    cell: (info) => (
      <Cell className="justify-end" info={info}>
        <FeatureActionsMenu feature={info.row.original} />
      </Cell>
    ),
    enableGlobalFilter: false,
    meta: options?.sticky ? { sticky: options.sticky } : undefined,
  });

const getFeatureColumns = (filterOptions: FilterOptions) => [
  queryColumnHelper.accessor(
    (row) =>
      row.columns.features?.map((feature) => feature.feature_key).join(" ") ??
      "",
    {
      id: "feature_key",
      header: (info) => <Header info={info}>Feature Key</Header>,
      cell: (info) => {
        const features = info.row.original.columns.features ?? [];
        return (
          <Cell className="h-auto" info={info}>
            <div className="flex min-h-fit flex-col items-start space-y-2">
              {features.map((feature) => (
                <Tooltip
                  key={feature.feature_key}
                  delay={250}
                  headerAction={
                    <CopyTextIcon
                      color="text-white hover:text-gray-500"
                      feedback="inline-only-checkmark"
                      value={feature.feature_key}
                    />
                  }
                  placement="top"
                  title={feature.feature_key}
                  asChild
                >
                  <Pill size="sm" variant="gray">
                    <Pill.Icon
                      icon={getTypeIcon(feature.feature_type ?? "", faMinus)}
                    />
                    <Pill.Text>
                      <HighlightedText
                        highlight={info.table.getState().globalFilter}
                        highlightClassName="font-inter-semibold-12px bg-yellow-200"
                        text={feature.feature_key}
                      />
                    </Pill.Text>
                  </Pill>
                </Tooltip>
              ))}
            </div>
          </Cell>
        );
      },
      minSize: 300,
      maxSize: 300,
      filterFn: "includesString",
      enableGlobalFilter: true,
      meta: { sticky: "left", displayName: "Key" },
      enableHiding: false,
    },
  ),
  queryColumnHelper.accessor(
    (row) => row.query_name || "Untitled Feature Query",
    {
      id: "query_name",
      header: (info) => <Header info={info}>Query Name</Header>,
      cell: (info) => <DisplaySqlCell info={info} />,
      minSize: 250,
      maxSize: 250,
      filterFn: "includesString",
      enableGlobalFilter: true,
    },
  ),
  queryColumnHelper.accessor(
    (row) =>
      row.columns.entities?.map((entity) => entity.entity_name).join(" ") ?? "",
    {
      id: "entities",
      header: (info) => (
        <Header info={info}>
          <div className="flex items-center gap-x-2">
            <span>Entity</span>
            <EntityFilter
              options={filterOptions.entityTypes}
              property="entities"
            />
          </div>
        </Header>
      ),
      cell: (info) => {
        const entities = info.row.original.columns.entities;
        return (
          <Cell info={info}>
            <EntityPills entities={entities?.map((e) => e.entity_name) ?? []} />
          </Cell>
        );
      },
      minSize: 150,
      maxSize: 150,
      filterFn: "includesString" as const,
      enableGlobalFilter: true,
    },
  ),
  queryColumnHelper.accessor("event", {
    header: (info) => (
      <Header info={info}>
        <div className="flex items-center gap-x-2">
          <span>Event</span>
          <EventTypeFilter options={filterOptions.eventTypes} />
        </div>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <Pill size="sm" variant="gray">
          <Pill.Icon icon={faCalculatorSimple} />
          <Pill.Text>{info.getValue()}</Pill.Text>
        </Pill>
      </Cell>
    ),
    minSize: 200,
    maxSize: 200,
  }),
  ...commonColumns<Query>().filter((column) => column.accessorKey !== "event"),
  ...statusColumns(queryColumnHelper, {
    getEnvironmentSettings: (row) => row?.environment_settings,
    hasQuery: (row) => !!row?.id,
    getQueryId: (row) => row?.id ?? "",
    getEtag: (row) => row?.etag ?? "",
    getEntities: (row) => row.columns.entities?.map((e) => e.entity_name) ?? [],
    getEvent: (row) => row?.event ?? "",
    getFeatures: (row) => row?.columns?.features ?? [],
  }),
  queryColumnHelper.display({
    id: "feature_default",
    header: (info) => (
      <Header info={info}>
        <span className="whitespace-nowrap">Default Value</span>
      </Header>
    ),
    cell: (info) => {
      const features = info.row.original.columns.features ?? [];
      return (
        <Cell className="h-auto" info={info}>
          <div className="flex min-h-fit flex-col items-start space-y-2">
            {features.map((feature) => (
              <span
                key={feature.feature_key}
                className="font-inter-regular-12px"
              >
                {speakPythonPrimitive(feature.feature_default ?? "None")}
              </span>
            ))}
          </div>
        </Cell>
      );
    },
    minSize: 130,
    maxSize: 130,
  }),
  queryColumnHelper.display({
    id: "feature_name",
    header: (info) => <Header info={info}>Feature Name</Header>,
    cell: (info) => {
      const features = info.row.original.columns.features ?? [];
      return (
        <Cell className="h-auto" info={info}>
          <div className="flex min-h-fit flex-col space-y-2">
            {features.map((feature) => (
              <div
                key={feature.feature_key}
                className="flex items-center gap-x-0.5"
              >
                <Tooltip placement="top" title={feature.name ?? ""} asChild>
                  <div className="truncate">
                    <HighlightedText
                      highlight={info.table.getState().globalFilter}
                      highlightClassName="font-inter-semibold-12px bg-yellow-200"
                      text={feature.name ?? ""}
                    />
                  </div>
                </Tooltip>
              </div>
            ))}
          </div>
        </Cell>
      );
    },
    minSize: 200,
    maxSize: 200,
    enableGlobalFilter: false,
  }),
  queryColumnHelper.display({
    id: "feature_description",
    header: (info) => <Header info={info}>Description</Header>,
    cell: (info) => {
      const features = info.row.original.columns.features ?? [];
      return (
        <div className="flex min-h-fit flex-col">
          {features.map((feature) => (
            <DisplayDescriptionCell
              key={feature.feature_key}
              info={info}
              value={feature.description}
            />
          ))}
        </div>
      );
    },
    minSize: 150,
    maxSize: 150,
    enableGlobalFilter: false,
  }),
  queryColumnHelper.display({
    id: "actions-menu",
    header: (info) => (
      <Header info={info}>
        <span />
      </Header>
    ),
    cell: (info) => (
      <Cell className="justify-end" info={info}>
        <TableActions query={info.row.original} />
      </Cell>
    ),
    minSize: 120,
    maxSize: 120,
    meta: { sticky: "right" },
  }),
];

const getColumns = (env: string) => [
  nameColumn("Feature name"),
  descriptionColumn(),
  keyColumn("Key"),
  columnHelper.accessor(
    (row) => {
      return row.query?.environment_settings?.[env]?.status;
    },
    {
      id: "environment_settings_status",
      header: (info) => (
        <Header info={info}>
          <div className="flex items-center gap-x-2">
            Status
            <StatusFilter column={info.column} />
          </div>
        </Header>
      ),
      cell: (info) => {
        const currentStatus = info.getValue();

        return currentStatus ? (
          <Cell info={info}>
            <QueryStatusPill status={currentStatus} />
          </Cell>
        ) : null;
      },
      enableGlobalFilter: false,
      filterFn: "equals",
    },
  ),
  featureDefaultColumn(),
  entityTypesColumn(),
  ...commonColumns<Feature>().map((column) =>
    columnHelper.accessor(`query.${column.accessorKey}` as keyof Feature, {
      header: (info) =>
        column.accessorKey === "event" ? (
          <Header info={info}>
            <div className="flex items-center gap-x-2">
              Event
              <EventTypeFilter column={info.column} />
            </div>
          </Header>
        ) : (
          column.header(info)
        ),
      cell: (info) => (info.row.original.query ? column.cell(info) : null),
      enableGlobalFilter: false,
    }),
  ),
  actionsMenuColumn(),
];

const FEATURE_FILTER_CONFIG = {
  entityFilterKey: "filter[entity_types][eq]",
  getEntityValues: (f: Feature) => f.entity_types ?? [],
  getEvent: (f: Feature) => f.query?.event ?? "",
  getSandboxStatus: (f: Feature) =>
    f.query?.environment_settings?.sandbox?.status,
  getLiveStatus: (f: Feature) => f.query?.environment_settings?.live?.status,
};

export const Features: React.FC<{
  openCreateModal: () => void;
  searchQuery: string;
  resetSearch: () => void;
}> = ({ openCreateModal, searchQuery, resetSearch }) => {
  const [environment] = useEnvironment();
  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );
  const { data: features, isLoading, isSuccess } = useFeatures({ environment });
  const {
    data: queriesData,
    isLoading: queriesLoading,
    isSuccess: queriesSuccess,
  } = useFeatureQueries();
  const queries = useMemo(
    () =>
      (queriesData?.filter((item): item is Query => !!item.id) ??
        []) as Query[],
    [queriesData],
  );
  const navigate = useNavigate();
  const { orgId, wsId } = useParamsDecode<DashboardPageParams>();
  const {
    filteredItems: filteredQueries,
    hasActiveFilters: hasActiveQueryFilters,
    resetFilters: resetQueryFilters,
  } = useFilteredData({
    items: queries ?? [],
    filterConfig: QUERY_FILTER_CONFIG,
  });

  const { filteredItems: filteredFeatures, hasActiveFilters } = useFilteredData(
    {
      items: features ?? [],
      filterConfig: FEATURE_FILTER_CONFIG,
    },
  );

  const filterOptions = useMemo(() => {
    if (featureLifecycleEnabled) {
      const entityTypes = [
        ...new Set(
          queries.flatMap(
            (q) => q.columns.entities?.map((e) => e.entity_name) ?? [],
          ),
        ),
      ];
      const eventTypes = [
        ...new Set(queries.map((q) => q.event).filter((e): e is string => !!e)),
      ];
      return { entityTypes, eventTypes };
    }
    const entityTypes = [
      ...new Set(features?.flatMap((f) => f.entity_types ?? []) ?? []),
    ];
    const eventTypes = [
      ...new Set(
        features?.map((f) => f.query?.event).filter((e): e is string => !!e) ??
          [],
      ),
    ];
    return { entityTypes, eventTypes };
  }, [features, queries, featureLifecycleEnabled]);

  const columns = useMemo((): ColumnDef<Feature | Query, unknown>[] => {
    if (featureLifecycleEnabled) {
      return getFeatureColumns(filterOptions) as ColumnDef<
        Feature | Query,
        unknown
      >[];
    }
    return getColumns(environment) as ColumnDef<Feature | Query, unknown>[];
  }, [environment, featureLifecycleEnabled, filterOptions]);

  const tableData: (Feature | Query)[] = featureLifecycleEnabled
    ? filteredQueries
    : filteredFeatures;

  const emptyStateNode = (
    <EmptyState
      actionButton={{
        icon: faPlus,
        text: "Create Feature",
        onClick: () => {
          openCreateModal();
        },
      }}
      dataLoc="features-empty-state"
      description="Features help you detect patterns and analyze behavior across your entities and events."
      headline="No features configured yet"
      icon={faCalculatorSimple}
    />
  );

  const activeFilters = featureLifecycleEnabled
    ? hasActiveQueryFilters
    : hasActiveFilters;

  const filterEmptyStateNode = (
    <EmptyState
      actionButton={
        activeFilters || searchQuery
          ? {
              text: "Clear filters",
              onClick: () => {
                if (featureLifecycleEnabled) {
                  resetQueryFilters();
                }
                // We need this clear search function for both featurelifecycle and the current view
                resetSearch();
              },
            }
          : undefined
      }
      description="Try adjusting your search or filters to find what you're looking for"
      headline="No matching features found"
      icon={faSearch}
    />
  );

  const showEmptyState = featureLifecycleEnabled
    ? !queriesLoading && queriesSuccess && !filteredQueries.length
    : !isLoading && isSuccess && !filteredFeatures.length;
  const isFilteredEmptyState =
    showEmptyState && (activeFilters || !!searchQuery);

  const isDataEmpty = featureLifecycleEnabled
    ? queriesSuccess && queries.length === 0
    : isSuccess && features?.length === 0;

  return (
    <>
      <WhitePane>
        <div className="flex h-full w-full flex-col gap-y-4">
          {isDataEmpty ? (
            emptyStateNode
          ) : (
            <div
              className={twJoin(
                featureLifecycleEnabled &&
                  "relative flex min-h-0 flex-1 flex-col p-4",
                featureLifecycleEnabled &&
                  showEmptyState &&
                  "items-center justify-center",
              )}
            >
              <TableComp
                columns={columns}
                data={tableData}
                dataLoc="features-list"
                emptyState={tableData.length > 0 && filterEmptyStateNode}
                frameClassName={twJoin(
                  "w-full",
                  featureLifecycleEnabled ? "min-h-0 flex-1" : "p-4",
                )}
                globalFilter={searchQuery}
                initialState={{
                  sorting: featureLifecycleEnabled
                    ? [
                        { id: "window_length", desc: false },
                        { id: "window_hop", desc: false },
                      ]
                    : [{ id: "key", desc: true }],
                }}
                isLoading={featureLifecycleEnabled ? queriesLoading : isLoading}
                rowPropsGetter={(row) => {
                  if (featureLifecycleEnabled) {
                    const query = row.original as Query;
                    return {
                      "data-loc": query.columns.features?.[0]?.feature_key,
                      onClick: () => {
                        navigate(
                          getUrlToManageFeatureQueryPage(
                            orgId,
                            wsId,
                            query.id,
                            "view",
                          ),
                        );
                      },
                      classNameOverrides:
                        "cursor-pointer hover:bg-gray-50 [&>td.sticky]:z-10 [&>td.sticky]:bg-white [&>td]:align-top [&_.sticky]:hover:bg-gray-50",
                    };
                  }
                  const feature = row.original as Feature;
                  return {
                    "data-loc": feature.key,
                  };
                }}
                variant={featureLifecycleEnabled ? "compact" : "default"}
                clientSideFiltering
              />
              {featureLifecycleEnabled &&
                showEmptyState &&
                (isFilteredEmptyState ? filterEmptyStateNode : emptyStateNode)}
            </div>
          )}
        </div>
      </WhitePane>
    </>
  );
};

const getFirstLineContent = (descriptionValue: string) => {
  if (descriptionValue.includes("\n")) {
    return `${descriptionValue.split("\n")[0]}...`;
  }
  return descriptionValue.length > 50
    ? `${descriptionValue.slice(0, 43)}...`
    : descriptionValue;
};

export const DisplayDescriptionCell = <D extends object, V>({
  info,
  value: descriptionValue,
}: {
  info: CellContext<D, V>;
  value: string | null | undefined;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const needsPopover =
    descriptionValue &&
    (descriptionValue.length > 50 || descriptionValue.includes("\n"));

  const firstLineContent = getFirstLineContent(descriptionValue ?? "");

  const content = (
    <div className="line-clamp-1 overflow-hidden">
      <MarkdownRenderer
        content={needsPopover ? firstLineContent : (descriptionValue ?? "")}
      />
    </div>
  );

  return needsPopover ? (
    <CustomPopover
      button={<Cell info={info}>{content}</Cell>}
      className="w-96 overflow-auto border-0"
      isOpen={isOpen}
      offsetX={0}
      placement="top"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {isOpen && descriptionValue && (
        <div className="p-3">
          <MarkdownRenderer content={descriptionValue} />
        </div>
      )}
    </CustomPopover>
  ) : (
    <Cell info={info}>{content}</Cell>
  );
};
